import { supabase } from '@/integrations/supabase/client';

/**
 * Client half of the outbound identity sync with StayYoung billing.
 *
 * The rule this enforces is "tell billing BEFORE saving locally": billing can
 * REFUSE a change, and a refusal that arrives after we have already written is
 * exactly the drift the whole feature exists to remove.
 *
 * The browser never talks to hub.stayyoung.live directly — see
 * `supabase/functions/sy-profile-updated`, which holds the shared secret and
 * resolves the caller's billing identity from their JWT.
 */

/**
 * Discriminated on a STRING, not on a boolean. This tsconfig sets
 * `strictNullChecks: false`, under which TypeScript will not narrow a union by a
 * boolean literal discriminant — callers would get the bare union and every
 * field access would fail to compile. (Same reasoning as `adminEmailService`.)
 */
export type ProfileSyncResult =
  /** Billing accepted, was already correct, or has no subscription for us. */
  | { status: 'ok' }
  /** Billing refused. Do NOT save. `message` is member-facing Hebrew. */
  | { status: 'refused'; reason: string; message: string };

interface ProxyBody {
  outcome?: string;
  may_save?: boolean;
  reason?: string;
  message?: string;
}

/**
 * Notify billing that the signed-in member renamed themselves.
 *
 * Returns `ok` for anything that is safe to persist — including the cases where
 * the call could not be made at all. A name is cosmetic and reversible, and the
 * edge function logs an `sy_profile_sync_failed` event when the wire fails, so
 * blocking a rename on a third-party outage would trade a self-healing drift for
 * a visible breakage. Refusals are the only thing that stops a save.
 */
export async function notifyBillingOfProfileEdit(params: {
  fullName: string | null;
}): Promise<ProfileSyncResult> {
  try {
    const { data, error } = await supabase.functions.invoke('sy-profile-updated', {
      body: { full_name: params.fullName },
    });

    if (error) {
      // Transport/HTTP failure. The function itself answers 200 for every
      // outcome it can reason about, so reaching here means we never got a
      // verdict — same reasoning as above: let the save through.
      console.error('[syProfileSync] proxy call failed', error);
      return { status: 'ok' };
    }

    const body = (data ?? {}) as ProxyBody;
    if (body.may_save === false) {
      return {
        status: 'refused',
        reason: typeof body.reason === 'string' ? body.reason : 'unknown',
        message:
          typeof body.message === 'string'
            ? body.message
            : 'העדכון נדחה על ידי מערכת החיובים. פנו לתמיכה.',
      };
    }
    return { status: 'ok' };
  } catch (err) {
    console.error('[syProfileSync] unexpected error', err);
    return { status: 'ok' };
  }
}
